import type { ReactNode } from "react";
import type { SurveyHeatLoss, SurveyRoom, SurveySurface } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { Async } from "./Async.jsx";

/**
 * One room's heat loss, element by element — where its watts go.
 *
 * The survey's heat loss already carries every surface's loss (area × U × ΔT)
 * and each room's ventilation and thermal-bridging loss; this groups them the
 * way a designer reads a room: floor, outside walls, party walls, internal
 * walls, windows, doors, roof, ventilation, bridging. A surface's group comes
 * from what is on its other side (the survey room's own surface record),
 * because "a wall" losing heat to outside and "a wall" losing it to a cooler
 * neighbour are different decisions.
 *
 * Read from the same `GET /api/surveys/:id/heat-loss` the Heat loss section
 * reads, keyed on `revision`, so a change on the page redraws it.
 */

type Group =
  | "floor"
  | "external-walls"
  | "party-walls"
  | "internal-walls"
  | "windows"
  | "doors"
  | "internal-doors"
  | "roof"
  | "ventilation"
  | "bridging";

const GROUP_WORDS: Readonly<Record<Group, string>> = {
  floor: "Floor",
  "external-walls": "Outside walls",
  "party-walls": "Party walls",
  "internal-walls": "Internal walls",
  windows: "Windows",
  doors: "Outside doors",
  "internal-doors": "Internal doors",
  roof: "Roof / ceiling",
  ventilation: "Ventilation",
  bridging: "Thermal bridging",
};

const ORDER: readonly Group[] = [
  "floor",
  "external-walls",
  "party-walls",
  "internal-walls",
  "windows",
  "doors",
  "internal-doors",
  "roof",
  "ventilation",
  "bridging",
];

function groupOf(kind: string, surface: SurveySurface | undefined): Group {
  if (kind === "floor") return "floor";
  if (kind === "roof" || kind === "ceiling" || kind === "rooflight") return "roof";
  if (kind === "window") return "windows";
  const adjacency = surface?.adjacency;
  if (kind === "door") return adjacency === "adjacent-room" || adjacency === "unheated-space" ? "internal-doors" : "doors";
  if (adjacency === "party") return "party-walls";
  if (adjacency === "adjacent-room" || adjacency === "unheated-space") return "internal-walls";
  return "external-walls";
}

const watts = (w: number): string => `${Math.round(w).toLocaleString("en-GB")} W`;

export function RoomHeatLossBreakdown({
  surveyId,
  room,
  revision = 0,
}: {
  readonly surveyId: string;
  readonly room: SurveyRoom;
  readonly revision?: number;
}): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<SurveyHeatLoss>(() => client.heatLoss(surveyId), [client, surveyId, revision]);

  return (
    <section className="room-heat-loss" aria-label={`Heat loss of ${room.name}`}>
      <h4>Heat loss by element</h4>
      <Async state={state} what="this room's heat loss" reload={reload}>
        {(survey) => {
          const rl = survey.heatLoss.rooms.find((r) => r.roomId === room.id);
          if (!rl) {
            return (
              <p className="muted small">
                {room.unheated
                  ? "Marked unheated, so it is not in the heat load."
                  : "Not in the heat loss yet — it needs a floor area and walls."}
              </p>
            );
          }
          const bySurface = new Map(room.surfaces.map((s) => [s.id, s] as const));
          const totals = new Map<Group, { w: number; areaM2: number }>();
          const add = (g: Group, w: number, areaM2 = 0) => {
            const t = totals.get(g) ?? { w: 0, areaM2: 0 };
            totals.set(g, { w: t.w + w, areaM2: t.areaM2 + areaM2 });
          };
          for (const s of rl.surfaces) add(groupOf(s.kind, bySurface.get(s.surfaceId)), s.heatLossW, s.areaM2);
          add("ventilation", rl.ventilationW);
          add("bridging", rl.thermalBridgingW);
          const rows = ORDER.filter((g) => (totals.get(g)?.w ?? 0) !== 0);
          const positive = rows.reduce((n, g) => n + Math.max(0, totals.get(g)!.w), 0);
          return (
            <>
              <dl className="room-heat-loss__figures">
                <div>
                  <dt>Total</dt>
                  <dd>{watts(rl.totalW)}</dd>
                </div>
                <div>
                  <dt>Per m² of floor</dt>
                  <dd>{Math.round(rl.wattsPerM2)} W/m²</dd>
                </div>
                <div>
                  <dt>Room at</dt>
                  <dd>{rl.designTempC} °C</dd>
                </div>
              </dl>
              {positive > 0 ? (
                <div className="room-heat-loss__bar" aria-hidden="true">
                  {rows
                    .filter((g) => totals.get(g)!.w > 0)
                    .map((g) => (
                      <span
                        key={g}
                        className={`room-heat-loss__seg room-heat-loss__seg--${g}`}
                        style={{ width: `${(totals.get(g)!.w / positive) * 100}%` }}
                        title={`${GROUP_WORDS[g]}: ${watts(totals.get(g)!.w)}`}
                      />
                    ))}
                </div>
              ) : null}
              <div className="table-scroll">
                <table className="room-heat-loss__table">
                  <thead>
                    <tr>
                      <th scope="col">Element</th>
                      <th scope="col" className="num">
                        Area
                      </th>
                      <th scope="col" className="num">
                        Loss
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((g) => (
                      <tr key={g}>
                        <th scope="row">
                          <span className={`room-heat-loss__key room-heat-loss__seg--${g}`} aria-hidden="true" />{" "}
                          {GROUP_WORDS[g]}
                        </th>
                        <td className="num">{totals.get(g)!.areaM2 > 0 ? `${totals.get(g)!.areaM2.toFixed(1)} m²` : "—"}</td>
                        <td className="num">{watts(totals.get(g)!.w)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="muted small">
                Ventilation: {room.airChangesPerHour.value} air changes an hour over {rl.volumeM3.toFixed(1)} m³.
              </p>
            </>
          );
        }}
      </Async>
    </section>
  );
}

/**
 * The whole house's heat loss, element by element — the room breakdown above
 * summed over every heated room: how much goes through the outside walls, the
 * windows, the roof, the floor, the air. Where a design's money is best spent.
 */
export function WholeHouseHeatLossByElement({
  surveyId,
  rooms,
  revision = 0,
}: {
  readonly surveyId: string;
  readonly rooms: readonly SurveyRoom[];
  readonly revision?: number;
}): ReactNode {
  const { client } = usePortal();
  const { state } = useAsync<SurveyHeatLoss>(() => client.heatLoss(surveyId), [client, surveyId, revision]);
  if (state.status !== "ready") return null;
  const bySurface = new Map(rooms.flatMap((r) => r.surfaces.map((s) => [s.id, s] as const)));
  const totals = new Map<Group, { w: number; areaM2: number }>();
  const add = (g: Group, w: number, areaM2 = 0) => {
    const t = totals.get(g) ?? { w: 0, areaM2: 0 };
    totals.set(g, { w: t.w + w, areaM2: t.areaM2 + areaM2 });
  };
  for (const rl of state.data.heatLoss.rooms) {
    // Heat passing between two heated rooms of the house cancels out across the
    // whole house; only what leaves it counts here.
    for (const s of rl.surfaces) {
      const g = groupOf(s.kind, bySurface.get(s.surfaceId));
      if (g !== "internal-walls" || bySurface.get(s.surfaceId)?.adjacency === "unheated-space") add(g, s.heatLossW, s.areaM2);
    }
    add("ventilation", rl.ventilationW);
    add("bridging", rl.thermalBridgingW);
  }
  const rows = ORDER.filter((g) => (totals.get(g)?.w ?? 0) > 0);
  if (rows.length === 0) return null;
  const sum = rows.reduce((n, g) => n + totals.get(g)!.w, 0);
  return (
    <section className="room-heat-loss whole-house-heat-loss" data-testid="whole-house-by-element" aria-label="Whole house heat loss by element">
      <h3>Whole house, by element</h3>
      <div className="room-heat-loss__bar" aria-hidden="true">
        {rows.map((g) => (
          <span
            key={g}
            className={`room-heat-loss__seg room-heat-loss__seg--${g}`}
            style={{ width: `${(totals.get(g)!.w / sum) * 100}%` }}
            title={`${GROUP_WORDS[g]}: ${watts(totals.get(g)!.w)}`}
          />
        ))}
      </div>
      <div className="table-scroll">
        <table className="room-heat-loss__table">
          <thead>
            <tr>
              <th scope="col">Element</th>
              <th scope="col" className="num">Area</th>
              <th scope="col" className="num">Loss</th>
              <th scope="col" className="num">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g}>
                <th scope="row">
                  <span className={`room-heat-loss__key room-heat-loss__seg--${g}`} aria-hidden="true" /> {GROUP_WORDS[g]}
                </th>
                <td className="num">{totals.get(g)!.areaM2 > 0 ? `${totals.get(g)!.areaM2.toFixed(1)} m²` : "—"}</td>
                <td className="num">{watts(totals.get(g)!.w)}</td>
                <td className="num">{Math.round((totals.get(g)!.w / sum) * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
